import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet, Search } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import PersonalInformeGlobal from './PersonalInformeGlobal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (s) => s ? s.split('-').reverse().join('/') : '';
const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

function periodRange(period) {
  const now = new Date();
  if (period === 'dia') return { from: iso(now), to: iso(now) };
  if (period === 'semana') {
    const ws = new Date(now); ws.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const we = new Date(ws); we.setDate(ws.getDate() + 6);
    return { from: iso(ws), to: iso(we) };
  }
  if (period === 'mes') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (period === 'anio') return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(new Date(now.getFullYear(), 11, 31)) };
  return null;
}

export default function PersonalInformes({ isAdmin, employees }) {
  const [mode, setMode] = useState('empleado');
  const [employeeId, setEmployeeId] = useState('');
  const [period, setPeriod] = useState('semana');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async () => {
    let range = period === 'rango' ? { from: customFrom, to: customTo } : periodRange(period);
    if (!range?.from || !range?.to) { toast.error('Selecciona el rango de fechas'); return; }
    if (isAdmin && !employeeId) { toast.error('Selecciona un empleado'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
      if (isAdmin && employeeId) params.append('employee_id', employeeId);
      const res = await axios.get(`${API}/personal/reports?${params.toString()}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el informe');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, employeeId, period, customFrom, customTo]);

  const rows = report ? report.days.map(d => [
    fmt(d.date), d.weekday,
    d.punches.map(p => `${p.type === 'in' ? 'E' : 'S'} ${fmtTime(p.time)}`).join('  '),
    d.worked_hours, d.expected_hours,
    `${d.diff >= 0 ? '+' : ''}${d.diff}`,
    d.holiday ? `Festivo: ${d.holiday}` : (d.leave_type === 'vacaciones' ? 'Vacaciones' : d.leave_type === 'dia_libre' ? 'Día libre' : (d.rest_day ? 'Descanso' : '')),
  ]) : [];

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(16); doc.setTextColor(15, 23, 42);
    doc.text('Informe de horas — Personal', 14, 16);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`${report.employee.name}${report.employee.position ? ' · ' + report.employee.position : ''}${report.employee.store_name ? ' · ' + report.employee.store_name : ''}`, 14, 23);
    doc.text(`Periodo: ${fmt(report.date_from)} — ${fmt(report.date_to)}`, 14, 29);
    autoTable(doc, {
      startY: 34,
      head: [['Fecha', 'Día', 'Fichajes', 'Trabajadas', 'Previstas', 'Dif.', 'Observaciones']],
      body: rows,
      foot: [['', '', 'TOTAL', report.totals.worked, report.totals.expected, `${report.totals.diff >= 0 ? '+' : ''}${report.totals.diff}`, `${report.totals.worked_days} días trab.`]],
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [15, 23, 42] },
      footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
    });
    doc.save(`informe_${report.employee.name.replace(/\s+/g, '_')}_${report.date_from}_${report.date_to}.pdf`);
  };

  const exportExcel = () => {
    if (!report) return;
    const data = [
      ['Informe de horas — Personal'],
      [report.employee.name, report.employee.position || '', report.employee.store_name || ''],
      [`Periodo: ${fmt(report.date_from)} — ${fmt(report.date_to)}`],
      [],
      ['Fecha', 'Día', 'Fichajes', 'Trabajadas', 'Previstas', 'Diferencia', 'Observaciones'],
      ...rows,
      [],
      ['', '', 'TOTAL', report.totals.worked, report.totals.expected, report.totals.diff, `${report.totals.worked_days} días trabajados`],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Informe');
    XLSX.writeFile(wb, `informe_${report.employee.name.replace(/\s+/g, '_')}_${report.date_from}_${report.date_to}.xlsx`);
  };

  const printReport = () => {
    if (!report) return;
    const win = window.open('', '_blank');
    const body = rows.map(r => `<tr>${r.map((c, i) => `<td style="${i >= 3 && i <= 5 ? 'text-align:right;' : ''}">${c}</td>`).join('')}</tr>`).join('');
    win.document.write(`<html><head><title>Informe</title><style>
      body{font-family:Arial,sans-serif;padding:20px;color:#0f172a}
      h1{font-size:18px;margin:0} p{color:#64748b;margin:4px 0;font-size:13px}
      table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12px}
      th,td{border:1px solid #e2e8f0;padding:5px 7px;text-align:left}
      th{background:#0f172a;color:#fff} tfoot td{font-weight:bold;background:#f1f5f9}
      @media print{@page{size:landscape;margin:12mm}}
    </style></head><body>
      <h1>Informe de horas — Personal</h1>
      <p>${report.employee.name}${report.employee.position ? ' · ' + report.employee.position : ''}${report.employee.store_name ? ' · ' + report.employee.store_name : ''}</p>
      <p>Periodo: ${fmt(report.date_from)} — ${fmt(report.date_to)}</p>
      <table><thead><tr><th>Fecha</th><th>Día</th><th>Fichajes</th><th>Trabajadas</th><th>Previstas</th><th>Dif.</th><th>Observaciones</th></tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr><td colspan="3">TOTAL</td><td style="text-align:right">${report.totals.worked}</td><td style="text-align:right">${report.totals.expected}</td><td style="text-align:right">${report.totals.diff >= 0 ? '+' : ''}${report.totals.diff}</td><td>${report.totals.worked_days} días trab.</td></tr></tfoot>
      </table></body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  const modeSwitch = isAdmin && (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="report-mode-switch">
      {[['empleado', 'Por empleado'], ['global', 'Toda la plantilla']].map(([k, label]) => (
        <button key={k} type="button" onClick={() => setMode(k)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${mode === k ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-800'}`}
          data-testid={`report-mode-${k}`}>
          {label}
        </button>
      ))}
    </div>
  );

  if (mode === 'global') {
    return (
      <div className="space-y-4">
        {modeSwitch}
        <PersonalInformeGlobal periodRange={periodRange} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {modeSwitch}
      {/* Controls */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          {isAdmin && (
            <div className="space-y-1.5">
              <Label>Empleado</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger className="w-56" data-testid="report-employee"><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Periodo</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="report-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dia">Día (hoy)</SelectItem>
                <SelectItem value="semana">Esta semana</SelectItem>
                <SelectItem value="mes">Este mes</SelectItem>
                <SelectItem value="anio">Este año</SelectItem>
                <SelectItem value="rango">Rango de fechas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === 'rango' && (
            <>
              <div className="space-y-1.5">
                <Label>Desde</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="report-from" />
              </div>
              <div className="space-y-1.5">
                <Label>Hasta</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="report-to" />
              </div>
            </>
          )}
          <Button onClick={generate} disabled={loading} className="bg-orange-500 hover:bg-orange-600" data-testid="generate-report-btn">
            <Search size={16} className="mr-2" />{loading ? 'Generando...' : 'Generar'}
          </Button>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Totals + export */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-6" data-testid="report-totals">
              <div><p className="text-xs uppercase font-bold text-slate-400">Trabajadas</p><p className="text-2xl font-bold text-slate-900">{report.totals.worked}h</p></div>
              <div><p className="text-xs uppercase font-bold text-slate-400">Previstas</p><p className="text-2xl font-bold text-slate-500">{report.totals.expected}h</p></div>
              <div><p className="text-xs uppercase font-bold text-slate-400">Diferencia</p><p className={`text-2xl font-bold ${report.totals.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>{report.totals.diff >= 0 ? '+' : ''}{report.totals.diff}h</p></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportPDF} data-testid="export-pdf-btn"><FileDown size={16} className="mr-2" />PDF</Button>
              <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn"><FileSpreadsheet size={16} className="mr-2" />Excel</Button>
              <Button variant="outline" onClick={printReport} data-testid="print-report-btn">Imprimir</Button>
            </div>
          </div>

          {/* Table */}
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">{report.employee.name} · {fmt(report.date_from)} — {fmt(report.date_to)}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="report-table">
                  <thead>
                    <tr className="bg-slate-50 text-xs uppercase text-slate-500">
                      <th className="text-left p-2.5">Fecha</th>
                      <th className="text-left p-2.5">Día</th>
                      <th className="text-left p-2.5">Fichajes</th>
                      <th className="text-right p-2.5">Trab.</th>
                      <th className="text-right p-2.5">Prev.</th>
                      <th className="text-right p-2.5">Dif.</th>
                      <th className="text-left p-2.5">Obs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.days.map(d => (
                      <tr key={d.date} className="border-t border-slate-100">
                        <td className="p-2.5">{fmt(d.date)}</td>
                        <td className="p-2.5 text-slate-500">{d.weekday}</td>
                        <td className="p-2.5">
                          {d.punches.length === 0 ? <span className="text-slate-300">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {d.punches.map((p, i) => (
                                <Badge key={i} variant="outline" className={`text-[11px] ${p.type === 'in' ? 'text-green-700 border-green-200' : 'text-red-700 border-red-200'}`}>
                                  {p.type === 'in' ? 'E' : 'S'} {fmtTime(p.time)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-2.5 text-right font-semibold">{d.worked_hours}</td>
                        <td className="p-2.5 text-right text-slate-500">{d.expected_hours}</td>
                        <td className={`p-2.5 text-right font-semibold ${d.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>{d.diff >= 0 ? '+' : ''}{d.diff}</td>
                        <td className="p-2.5 text-xs text-slate-500">
                          {d.holiday && <span className="text-red-600">Festivo: {d.holiday}</span>}
                          {d.leave_type === 'vacaciones' && <span className="text-blue-600">Vacaciones</span>}
                          {d.leave_type === 'dia_libre' && <span className="text-purple-600">Día libre</span>}
                          {!d.holiday && !d.leave_type && d.rest_day && <span className="text-slate-400">Descanso</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                      <td className="p-2.5" colSpan={3}>TOTAL ({report.totals.worked_days} días trabajados)</td>
                      <td className="p-2.5 text-right">{report.totals.worked}</td>
                      <td className="p-2.5 text-right">{report.totals.expected}</td>
                      <td className={`p-2.5 text-right ${report.totals.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>{report.totals.diff >= 0 ? '+' : ''}{report.totals.diff}</td>
                      <td className="p-2.5" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
